import { Field, Form, Formik } from 'formik';
import { useRouter } from '@uirouter/react';

import {
  AuthTypeOption,
  Provider,
} from '@/react/portainer/account/git-credentials/types';
import { GitCredentialSection } from '@/react/portainer/account/git-credentials/components/GitCredentialSection';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { Button } from '@@/buttons';

import {
  CredentialType,
  credentialTitles,
  GitCredentialFormValues,
} from '../../types';

import { validationSchema } from './GitCredentialsForm.validation';

type Props = {
  selectedProvider: CredentialType;
  isEditing?: boolean;
  isLoading: boolean;
  onSubmit: (formValues: GitCredentialFormValues) => void;
  credentialNames: string[];
  initialValues?: GitCredentialFormValues;
};

const defaultInitialValues: GitCredentialFormValues = {
  name: '',
  credentials: {
    username: '',
    personalAccessToken: '',
    provider: Provider.Custom,
    authType: AuthTypeOption.Basic,
    urlPattern: '',
  },
};

export function GitCredentialsForm({
  selectedProvider,
  isEditing = false,
  isLoading,
  onSubmit,
  credentialNames,
  initialValues = defaultInitialValues,
}: Props) {
  const router = useRouter();
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={() => validationSchema(credentialNames, isEditing)}
      onSubmit={(values) => onSubmit(values)}
      validateOnMount
    >
      {({
        values,
        errors,
        touched,
        handleSubmit,
        isValid,
        dirty,
        setFieldValue,
      }) => (
        <Form className="form-horizontal" onSubmit={handleSubmit} noValidate>
          <FormControl
            inputId="name"
            label="Credentials name"
            size="medium"
            errors={touched.name ? errors.name : undefined}
            required
          >
            <Field
              as={Input}
              name="name"
              id="name"
              maxLength={200}
              value={values.name}
              placeholder={`e.g. ${credentialTitles[selectedProvider]}-staging`}
              data-cy="cloudSettings-apiKeyName"
            />
          </FormControl>

          <GitCredentialSection
            provider={values.credentials.provider}
            authorizationType={values.credentials.authType}
            username={values.credentials.username ?? ''}
            password={values.credentials.personalAccessToken}
            isEditing={isEditing}
            errors={{
              username: touched.credentials?.username
                ? errors.credentials?.username
                : undefined,
              password: touched.credentials?.personalAccessToken
                ? errors.credentials?.personalAccessToken
                : undefined,
            }}
            onChange={({ provider, username, password, authorizationType }) => {
              if (provider !== undefined) {
                setFieldValue('credentials.provider', provider);
              }
              if (username !== undefined) {
                setFieldValue('credentials.username', username);
              }
              if (password !== undefined) {
                setFieldValue('credentials.personalAccessToken', password);
              }
              if (authorizationType !== undefined) {
                setFieldValue('credentials.authType', authorizationType);
              }
            }}
          />

          <FormControl
            inputId="urlPattern"
            label="Allowed repository URL pattern(s)"
            size="medium"
            errors={
              touched.credentials?.urlPattern
                ? errors.credentials?.urlPattern
                : undefined
            }
            required
            tooltip="Enter one or more comma-separated glob patterns, e.g. github.com/my-org/*. These patterns limit which repository URLs can use this credential. Each pattern matches one path segment, so * does not match across a /. Exception: a pattern of only * (or scheme://*) matches every URL."
          >
            <Field
              as={Input}
              name="credentials.urlPattern"
              id="urlPattern"
              value={values.credentials.urlPattern}
              placeholder="e.g. github.com/my-org/*"
              data-cy="cloudSettings-gitUrlPattern"
            />
          </FormControl>

          <div className="form-group">
            <div className="col-sm-12 mt-3">
              <LoadingButton
                disabled={!isValid || !dirty}
                data-cy="createCredentials-saveButton"
                isLoading={isLoading}
                className="!ml-0"
                loadingText="Saving credentials..."
              >
                {isEditing ? 'Update credentials' : 'Add credentials'}
              </LoadingButton>
              {isEditing && (
                <Button
                  color="default"
                  data-cy="createGitCredentials-cancelButton"
                  onClick={() =>
                    router.stateService.go(
                      'portainer.settings.sharedcredentials'
                    )
                  }
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
